import { z } from 'zod';
import {
  HarEntrySchema,
  WebGrpcDecodedDataSchema,
} from '@terra/shared-schemas-tracker';
import { EndpointSourceSchema } from '@terra/shared-schemas-orchestrator';
import { normalizeHarHttpVersion } from '@terra/shared-utils';
import {
  TRACKER_BULK_REQUEST_LIMIT,
  TRACKER_SKIP_REASON_OUT_OF_SCOPE,
} from './tracker.constants';

const HarHttpVersionSchema = z
  .string()
  .transform((value) => normalizeHarHttpVersion(value));

export const WebSocketMessageSampleSchema = z.object({
  direction: z.enum(['send', 'receive']),
  opcode: z.string(),
  time: z.number().optional(),
  data: z.string(),
  truncated: z.boolean().optional(),
});

export const BoundedWireSampleSchema = z.object({
  direction: z.enum(['request', 'response']),
  encoding: z.enum(['utf8', 'base64']),
  data: z.string().max(8192),
  truncated: z.boolean().optional(),
  contentType: z.string().optional(),
});

export const GrpcWireMetadataSchema = z.object({
  family: z.enum(['grpc-web', 'connect']),
  framing: z.enum([
    'grpc-web',
    'grpc-web-text',
    'connect',
    'connect-streaming',
  ]),
  interaction: z.enum([
    'unary',
    'server_streaming',
    'client_streaming',
    'bidi',
  ]),
  serialization: z.enum(['protobuf', 'json']),
  serviceName: z.string().optional(),
  methodName: z.string().optional(),
});

export const NetworkRequestSchema = z.object({
  applicationId: z.string(),
  credentialId: z.string().nullable().optional(),
  pageUrl: z.string().optional(), // URL of the page where this HAR entry originated
  crawlerSessionId: z.string().optional(), // Crawler session ID for stats tracking
  crawlObservationId: z.string().optional(),
  trackingSessionId: z.string().optional(),
  endpointSource: EndpointSourceSchema,
  endpointCandidateId: z.string().optional(), // Probed endpoint candidate that produced this HAR entry
  webSocketMessages: z.array(WebSocketMessageSampleSchema).optional(),
  grpcMetadata: GrpcWireMetadataSchema.optional(),
  wireSamples: z.array(BoundedWireSampleSchema).max(2).optional(),
  isClosed: z.boolean().optional(),
  allowNotFoundResponse: z.boolean().optional(),
  request: z.object({
    method: z.string(),
    url: z.string(),
    httpVersion: HarHttpVersionSchema,
    cookies: z.array(
      z.object({
        name: z.string(),
        value: z.string(),
        path: z.string().optional(),
        domain: z.string().optional(),
        expires: z.string().optional(),
        httpOnly: z.boolean().optional(),
        secure: z.boolean().optional(),
        comment: z.string().optional(),
      }),
    ),
    headers: z.array(
      z.object({
        name: z.string(),
        value: z.string(),
        comment: z.string().optional(),
      }),
    ),
    queryString: z.array(
      z.object({
        name: z.string(),
        value: z.string(),
        comment: z.string().optional(),
      }),
    ),
    postData: z
      .object({
        mimeType: z.string(),
        params: z
          .array(
            z.object({
              name: z.string(),
              value: z.string().optional(),
              fileName: z.string().optional(),
              contentType: z.string().optional(),
              comment: z.string().optional(),
            }),
          )
          .optional(),
        text: z.string().optional(),
        comment: z.string().optional(),
        encoding: z.enum(['utf8', 'base64']).optional(),
      })
      .optional(),
    headersSize: z.number(),
    bodySize: z.number(),
    comment: z.string().optional(),
  }),
  response: z.object({
    status: z.number(),
    statusText: z.string(),
    httpVersion: HarHttpVersionSchema,
    cookies: z.array(
      z.object({
        name: z.string(),
        value: z.string(),
        path: z.string().optional(),
        domain: z.string().optional(),
        expires: z.string().optional(),
        httpOnly: z.boolean().optional(),
        secure: z.boolean().optional(),
        comment: z.string().optional(),
      }),
    ),
    headers: z.array(
      z.object({
        name: z.string(),
        value: z.string(),
        comment: z.string().optional(),
      }),
    ),
    content: z.object({
      size: z.number(),
      mimeType: z.string(),
      text: z.string().optional().nullable(),
      encoding: z.string().optional(),
      comment: z.string().optional(),
    }),
    redirectURL: z.string(),
    headersSize: z.number(),
    bodySize: z.number(),
    comment: z.string().optional(),
    _transferSize: z.number().nullable().optional(),
  }),
  timings: z.object({
    blocked: z.number().optional(),
    dns: z.number().optional(),
    connect: z.number().optional(),
    send: z.number().optional(),
    wait: z.number(),
    receive: z.number(),
    ssl: z.number().optional(),
    comment: z.string().optional(),
  }),
});

export const NetworkRequestBatchSchema = z.object({
  applicationId: z.string(),
  requests: z
    .array(NetworkRequestSchema)
    .min(1)
    .max(TRACKER_BULK_REQUEST_LIMIT),
});

export const TorchSeenNetworkInteractionSchema = z.object({
  applicationId: z.string(),
  credentialId: z.string().nullable().optional(),
  trackingSessionId: z.string().optional(),
  requestId: z.string().optional(),
  kind: z.enum(['electron_filter', 'out_of_scope', 'store_skip']),
  reason: z.string().max(500).optional(),
  responseStatusCode: z.number().int().optional(),
  request: z.object({
    method: z.string(),
    url: z.string(),
  }),
});

export const TorchSeenNetworkInteractionResponseSchema = z.object({
  success: z.boolean(),
});

export const TrackerSkipReasonSchema = z.enum([
  TRACKER_SKIP_REASON_OUT_OF_SCOPE,
]);

export type TrackerSkipReason = z.infer<typeof TrackerSkipReasonSchema>;

export type TorchSeenNetworkInteractionDto = z.infer<
  typeof TorchSeenNetworkInteractionSchema
>;

export const TrackerReportResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  skipReason: TrackerSkipReasonSchema.optional(),
});

export const TrackerBatchReportResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  skipReason: TrackerSkipReasonSchema.optional(),
  trackedCount: z.number(),
  failedCount: z.number(),
  skippedCount: z.number().optional(),
  outOfScopeSkippedCount: z.number().optional(),
});

export const TrackingDataResponseSchema = z.array(HarEntrySchema);

// Page tracking schema for webpage capture
export const PageTrackingSchema = z.object({
  applicationId: z.string(),
  credentialId: z.string().nullable(), // Required: string or null (not optional)
  crawlerSessionId: z.string().optional(), // Manual crawler session ID for attribution
  url: z.string(),
  title: z.string(),
  html: z.string(),
  screenshot: z.string(), // base64 encoded image
  description: z.string().optional(),
  linksInternal: z.array(z.string()).default([]),
  linksExternal: z.array(z.string()).default([]),
  cookies: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
        domain: z.string().optional(),
        path: z.string().optional(),
        expires: z.string().optional(),
        httpOnly: z.boolean().optional(),
        secure: z.boolean().optional(),
      }),
    )
    .default([]),
  headers: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
      }),
    )
    .default([]),
});

export const PageTrackingResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

// Tool execution HAR schemas
export const ToolExecutionHarSchema = z.object({
  id: z.string().describe('Unique ID of this captured HTTP exchange (HAR).'),
  tenantId: z.string().optional(),
  applicationId: z
    .string()
    .describe('ID of the application this evidence belongs to.'),
  credentialId: z.string().optional(),
  matchedCredentialId: z.string().optional(),
  threadId: z.string(),
  toolCallId: z.string(),
  parentToolCallId: z.string().optional(),
  executionId: z.string(),
  sessionId: z.string(),
  toolType: z.enum(['HTTP', 'PYTHON']),
  toolName: z.string(),
  harEntry: z
    .object({
      startedDateTime: z.string(),
      time: z.number(),
      request: z.object({
        method: z.string(),
        url: z.string(),
        httpVersion: HarHttpVersionSchema,
        headers: z.array(z.object({ name: z.string(), value: z.string() })),
        cookies: z.array(z.object({ name: z.string(), value: z.string() })),
        queryString: z.array(z.object({ name: z.string(), value: z.string() })),
        postData: z
          .object({
            mimeType: z.string(),
            text: z.string(),
            comment: z.string().optional(),
          })
          .optional(),
        headersSize: z.number(),
        bodySize: z.number(),
      }),
      response: z.object({
        status: z.number(),
        statusText: z.string(),
        httpVersion: HarHttpVersionSchema,
        headers: z.array(z.object({ name: z.string(), value: z.string() })),
        cookies: z.array(z.object({ name: z.string(), value: z.string() })),
        content: z.object({
          size: z.number(),
          mimeType: z.string(),
          text: z.string(),
          encoding: z.enum(['utf8', 'base64']).optional(),
        }),
        redirectURL: z.string(),
        headersSize: z.number(),
        bodySize: z.number(),
      }),
      timings: z.object({
        send: z.number().optional(),
        wait: z.number(),
        receive: z.number(),
      }),
      cache: z.object({}).optional(),
      webGrpcDecodedData: WebGrpcDecodedDataSchema.nullable().optional(),
    })
    .describe('The captured HTTP request/response in HAR entry format.'),
  redirectChainId: z
    .string()
    .optional()
    .describe(
      'Identifier shared by every HAR captured while following one redirect chain for the same request.',
    ),
  redirectHopIndex: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe(
      'Zero-based position of this HAR within its redirect chain, ordered from the initial request to the final response.',
    ),
  timestamp: z.string(),
  createdAt: z.string(),
});

export type ToolExecutionHar = z.infer<typeof ToolExecutionHarSchema>;

export const ToolExecutionHarsResponseSchema = z.object({
  hars: z.array(ToolExecutionHarSchema),
  count: z.number(),
});

export const ToolHarPresenceByThreadResponseSchema = z.object({
  toolCallIds: z
    .array(z.string())
    .describe(
      'Tool call IDs within the thread that have at least one captured HTTP artifact (HAR).',
    ),
  count: z.number(),
});

export type ToolHarPresenceByThread = z.infer<
  typeof ToolHarPresenceByThreadResponseSchema
>;

export const DecodeGrpcHarPreviewBodySchema = z.object({
  applicationId: z.string(),
  request: z.unknown(),
  response: z.unknown(),
});

export const DecodeGrpcHarPreviewResponseSchema = z.object({
  webGrpcDecodedData: WebGrpcDecodedDataSchema.nullable(),
});

export const EncodeGrpcHarPreviewBodySchema = z.object({
  applicationId: z.string(),
  request: z.unknown(),
  response: z.unknown(),
  decodedRequest: z.record(z.string(), z.unknown()),
});

export const EncodeGrpcHarPreviewResponseSchema = z.object({
  wireRequestText: z.string(),
  wireRequestEncoding: z.enum(['base64']).optional(),
});
